import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import DataTable from '../components/DataTable';
import Badge from '../bookings/Badge';
import PainterDrawer from './PainterDrawer';
import { confidence_color, fmt_dt, fmt_usd, license_color, painter_name, safe_http_url } from './helpers';
import type { DatasetBusinessesResponse, DatasetDetailRecord, PainterRow } from './types';

// Renders the dataset brief/summary, which are STRUCTURED objects on the wire (not strings) —
// a plain `{value}` would throw "Objects are not valid as a React child" and blank the page.
// String → text; anything else → pretty JSON.
function TextOrJson({ value }: { value: unknown }) {
  if (value == null) return null;
  if (typeof value === 'string') return <p style={{ margin: '4px 0 0' }}>{value}</p>;
  return (
    <pre
      style={{
        margin: '4px 0 0',
        whiteSpace: 'pre-wrap',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 12,
        background: 'var(--green-50)',
        border: '1px solid var(--line)',
        borderRadius: 8,
        padding: 10,
        overflowX: 'auto',
      }}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

// A dataset's painters (spec §5 detail view: list + drawer, mirroring bookings). The header
// comes from /datasets/:id; the painter rows from /datasets/:id/businesses (badges already
// resolved, no per-row fetch). Row click opens the drawer, which lazy-loads claims+conflicts.
// A bad dataset id is a 404 → "dataset not found"; a valid-but-empty dataset is a clean empty.
export default function DatasetDetail() {
  const { dataset_id = '' } = useParams();
  const [open_id, set_open_id] = useState<string | null>(null);

  const header = useQuery({
    queryKey: ['b360_dataset', dataset_id],
    queryFn: () => api.get<DatasetDetailRecord>(`/api/business360/datasets/${encodeURIComponent(dataset_id)}`),
    retry: false,
  });

  const list = useQuery({
    queryKey: ['b360_dataset_businesses', dataset_id],
    queryFn: () =>
      api.get<DatasetBusinessesResponse>(`/api/business360/datasets/${encodeURIComponent(dataset_id)}/businesses`),
    retry: false,
  });

  const not_found =
    (header.error instanceof ApiError && header.error.status === 404) ||
    (list.error instanceof ApiError && list.error.status === 404);

  const columns = [
    {
      header: 'Painter',
      cell: (p: PainterRow) => (
        <span>
          <strong>{painter_name(p)}</strong>
          {p.city ? <span className="muted" style={{ fontSize: 13 }}> · {p.city}</span> : null}
        </span>
      ),
    },
    {
      header: 'Phone',
      cell: (p: PainterRow) => (p.phone_primary ? <code style={{ fontSize: 13 }}>{p.phone_primary}</code> : '—'),
    },
    {
      header: 'Website',
      cell: (p: PainterRow) => {
        const href = safe_http_url(p.website);
        return href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            link
          </a>
        ) : (
          '—'
        );
      },
      width: '90px',
    },
    {
      header: 'License',
      cell: (p: PainterRow) =>
        p.license_status ? (
          <Badge color={license_color(p.license_status)}>
            {p.license_status}
            {p.license_class ? ` · ${p.license_class}` : ''}
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      header: 'Build-ready',
      cell: (p: PainterRow) => (p.build_ready ? <Badge color="#1f7a44">yes</Badge> : <span className="muted">no</span>),
      width: '110px',
    },
    {
      header: 'Confidence',
      cell: (p: PainterRow) =>
        p.confidence_overall ? (
          <Badge color={confidence_color(p.confidence_overall)}>{p.confidence_overall}</Badge>
        ) : (
          '—'
        ),
      width: '120px',
    },
  ];

  return (
    <div className="container">
      <p style={{ marginBottom: 8 }}>
        <Link to="/business360" className="muted">
          ← All datasets
        </Link>
      </p>

      {not_found ? (
        <div className="card" style={{ padding: 24 }}>
          <h1 style={{ marginTop: 0 }}>Dataset not found</h1>
          <p className="muted" style={{ margin: 0 }}>
            No Business 360 dataset with id <code>{dataset_id}</code>.
          </p>
        </div>
      ) : (
        <>
          <h1 style={{ marginBottom: 0, textTransform: 'capitalize' }}>
            {header.data?.vertical ?? 'Dataset'}
          </h1>
          <p className="muted" style={{ marginTop: 4, marginBottom: 16 }}>
            <code>{dataset_id}</code>
            {header.data ? (
              <>
                {' '}· {fmt_dt(header.data.created_at)} · {header.data.business_ids.length} records ·{' '}
                {fmt_usd(header.data.variable_cost_usd)}
              </>
            ) : null}
          </p>
          {(header.data?.brief != null || header.data?.summary != null) && (
            <div className="card" style={{ marginBottom: 16 }}>
              {header.data?.brief != null && (
                <>
                  <strong style={{ fontSize: 13 }}>Brief</strong>
                  <TextOrJson value={header.data.brief} />
                </>
              )}
              {header.data?.summary != null && (
                <>
                  <strong style={{ fontSize: 13, display: 'block', marginTop: 12 }}>Summary</strong>
                  <TextOrJson value={header.data.summary} />
                </>
              )}
            </div>
          )}

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {list.isLoading && <p style={{ padding: 24, margin: 0 }}>Loading painters…</p>}
            {list.error && !not_found && (
              <p className="error" style={{ margin: 24 }}>
                Failed to load: {list.error instanceof ApiError ? list.error.reason : String(list.error)}
              </p>
            )}
            {list.data && (
              <DataTable
                rows={list.data.businesses}
                columns={columns}
                on_row_click={(p) => set_open_id(p.business_id)}
                empty_message="No painters in this dataset yet."
              />
            )}
          </div>
        </>
      )}

      {open_id && <PainterDrawer business_id={open_id} on_close={() => set_open_id(null)} />}
    </div>
  );
}
